import { foldTypographyWithMap } from "./document_patch_compiler";
import { ATOM_KINDS } from "./document_text_index";

/**
 * Place drafter notes in the lease body instead of a side panel.
 *
 * A note exists because a proposal could not be turned into a tracked change:
 * the quote did not ground, the language it targets is template-static, the
 * value has no field control. None of those are reasons the underlying deal
 * term stops mattering. The landlord imported the source document because they
 * agreed with it, and they own the template — so there is always somewhere in
 * the lease this belongs, and a list off to the side is the one place it does
 * not belong.
 *
 * Every note therefore gets a position. The ladder degrades, it never refuses:
 *
 *   1. `anchor`        — the lease wording the proposal quoted.
 *   2. `contextBefore` — the wording it quoted as preceding context. A proposal
 *                        that failed on its anchor often still cited real
 *                        context, and context is a position by construction.
 *   3. `field`         — the deal-terms field the note names, or an automated
 *                        field its quotes referenced. Either resolves through
 *                        the atom table, so this rung needs no text match at
 *                        all and no wording can defeat it.
 *   4. `document-end`  — the last block. Nothing is dropped for want of a home.
 *
 * The rung is reported so a run that lands everything on `document-end` is
 * visible as a degradation rather than passing for a clean placement.
 */

// A prefix, not a whole token: a quote may cite an atom without carrying the
// closing bracket. The kinds come from the projection so a note can resolve
// through a tracked change the same way it resolves through a lease-var.
const ATOM_REFERENCE = new RegExp(`⟦([${ATOM_KINDS}])(\\d+):`);

// Written for a commercial real estate attorney reading the lease itself, not
// a support channel. The note lands in the document body, so it says what the
// drafter has to do about it — a restatement of which internal check refused
// would be noise at the point of drafting.
const NOTE_ACTIONS = {
  quote_not_found:
    "This term from the source document has no matching language in the lease yet.",
  grounding_failed:
    "This term from the source document could not be matched to a place in the lease.",
  source_quote_unverified:
    "This restates the source document rather than quoting it, so check it against the original.",
  ambiguous:
    "This term matches more than one place in the lease, so it has to be updated by hand.",
  not_editable:
    "The lease language for this term is fixed template text, so it has to be changed by hand.",
  requires_override:
    "The lease language for this term comes from the form template. Changing it needs an Override.",
  atom_reorder:
    "This term moves a value LeasePilot fills in automatically. Update it in the deal terms.",
  mutates_atom:
    "This term edits a value LeasePilot fills in automatically. Update it in the deal terms.",
  field_conflict:
    "This term changes a value LeasePilot fills in automatically. Update it in the deal terms.",
  no_control:
    "This value could not be filled in automatically. Set it in the deal terms, or by hand here.",
  read_only:
    "This value cannot be set from the deal terms in this form, so it has to be written into the lease text.",
  cross_reference_block:
    "Other parts of the lease refer to this paragraph, so removing it has to be done by hand.",
  cross_reference_target:
    "This needs a reference to a section that does not exist yet.",
  overlapping_patch:
    "Another suggestion already changes this same wording, so this one was left for you.",
  table_row_not_found:
    "This affects a table row that could not be matched automatically.",
  table_row_shape:
    "This does not match the columns of the table it belongs in.",
  unsupported_list_template:
    "This form has no paragraph style that can carry this new language.",
  low_confidence:
    "This reading of the source document was not certain enough to apply on its own.",
  invalid_proposal:
    "Part of the source document could not be read into a usable suggestion.",
  // Distinct from the others on purpose: this proposal cited nothing from the
  // source at all, so unlike every reason above it the note cannot claim the
  // source document asked for anything.
  source_quote_missing:
    "This suggestion did not point to anything in the source document, so treat it as a prompt to check rather than a term to draft."
};

const DEFAULT_ACTION =
  "This term from the source document needs a drafter’s attention.";

/**
 * Refusals the drafter should get a clause for rather than a note about.
 *
 * The line is where the place is known but the mechanism is not available.
 * Every reason here located its target — the passage exists, or the field
 * resolves through the atom table — and failed only because this form does not
 * let anyone edit that passage, or holds no control for that value. The term
 * still belongs in the lease, and a rider paragraph beside language that cannot
 * be touched is what a drafter would write anyway.
 *
 * Deliberately excludes `quote_not_found` and `ambiguous`. Those failed on
 * WHERE, and the placement ladder's answer for them is a guess. A guess is a
 * fine place to leave a note asking someone to look; it is a bad place to put
 * lease language that could be accepted into the agreement without anyone
 * re-reading its surroundings.
 *
 * The sentence is shown on the card, so it says why this arrived as a separate
 * paragraph — a drafter who cannot see that will wonder why we did not simply
 * change the number in front of them.
 */
const DRAFTABLE_REFUSALS = {
  not_editable:
    "The lease language for this term is fixed template text, so this states the term as a new paragraph beside it.",
  requires_override:
    "The lease language for this term comes from the form template, so this states the term as a new paragraph beside it. Changing the template itself needs an Override.",
  no_control:
    "This value has no deal-terms field in this form, so it is written into the lease text instead. If it should be a field, it needs one adding to the form.",
  read_only:
    "This value cannot be set from the deal terms in this form, so it is written into the lease text instead.",
  unresolved_path:
    "This value could not be tied to a deal term in this form, so it is written into the lease text instead. If it should be a field, it needs one adding to the form.",
};

export const DRAFTABLE_REFUSAL_REASONS = Object.freeze(
  Object.keys(DRAFTABLE_REFUSALS)
);

/**
 * Refusals where the anchor failed but the clause did not.
 *
 * A text_patch that cannot find its quote does not know what to rewrite, and
 * guessing would edit the wrong words. An inserted clause is not like that: its
 * anchor is a pointer to the paragraph it should follow, nothing in the lease
 * was going to be rewritten, and the language it drafted stands on its own. So
 * a failed anchor costs placement accuracy, not correctness — which is the
 * distinction `content` marks, since only an inserted block ever carries it.
 *
 * That leaves one question, and the placement ladder answers it rather than
 * asking us to assume: every rung except `document-end` matched real text in
 * this document. Where one of those matched we have a position we can defend.
 * Where none did, the end of the lease is where the note goes and no clause
 * belongs there.
 *
 * `ambiguous` is deliberately absent. Its anchor matched several real places,
 * so choosing one silently is a judgment about which the drafter would want —
 * different from placing a clause whose anchor matched nowhere.
 */
const ANCHOR_FAILURE_REASONS = ["grounding_failed", "quote_not_found"];

// Says which part is uncertain. The content is not in doubt; the position is,
// and the position is the part a drafter fixes in one click with INSERT HERE.
const ANCHOR_FAILURE_NOTICE =
  "The lease paragraph this was meant to follow could not be matched, so it is placed by its surrounding context — check that it sits in the right place.";

// Exported so a reason the compiler can emit but this table never learned about
// fails a test rather than silently falling back to DEFAULT_ACTION.
export const NOTE_ACTION_REASONS = Object.freeze(Object.keys(NOTE_ACTIONS));

function collapseWhitespace(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * The sentence the drafter reads inside the lease. The annotation card carries
 * the same evidence in its Source / Source quote / Rationale rows, but the note
 * has to stand on its own once accepted: the card disappears when review does.
 *
 * Bracketed, in the drafting convention this audience already reads as "this is
 * not lease language" — the same signal the templates use for [ADD FOR ALCOHOL]
 * and friends. Once accepted the note is plain text in the document, so the
 * brackets are the only thing left saying it is not part of the agreement.
 */
export function noteBodyText(note) {
  const action = NOTE_ACTIONS[note && note.reason] || DEFAULT_ACTION;

  // Deliberately just the instruction. The source quote and the rationale are
  // on the card directly below, and repeating them here put a paragraph of
  // provenance chatter into the body of a lease — text that reads as though the
  // system is talking about itself rather than telling a drafter what to do.
  return `[DRAFTING NOTE — ${action}]`;
}

function pidHost(node) {
  const element =
    node && node.nodeType === 3 ? node.parentElement : node || null;
  return element && typeof element.closest === "function"
    ? element.closest("[pid]")
    : null;
}

function spanAt(index, offset) {
  return (index.spans || []).find(
    span => span.start <= offset && offset < span.end
  );
}

function elementForQuote(index, quote) {
  const folded = collapseWhitespace(quote);
  if (!folded) return null;

  const haystack = foldTypographyWithMap(index.text, true);
  const needle = foldTypographyWithMap(quote, true).text;
  if (!needle) return null;

  // Unlike `locate`, a second occurrence is not fatal here. Nothing is edited
  // and nothing is claimed to be unique — the note only needs to land near the
  // language it is about, and the first occurrence is near it.
  const position = haystack.text.indexOf(needle);
  if (position === -1) return null;

  const span = spanAt(index, haystack.offsets[position].start);
  return span ? pidHost(span.node) : null;
}

function elementForAtomReference(index, ...quotes) {
  for (const quote of quotes) {
    const match = ATOM_REFERENCE.exec(String(quote || ""));
    if (!match) continue;

    // Kind AND ordinal. Automation atoms are numbered in document order while a
    // tracked change carries its diff-index, so the two occupy separate
    // numbering spaces and ⟦f3:…⟧ and ⟦d3:…⟧ are different things in the same
    // table. Matching on the ordinal alone resolves whichever came first.
    const kind = match[1];
    const ordinal = Number(match[2]);
    const atom = (index.atomSpans || []).find(
      span => span.kind === kind && span.ordinal === ordinal
    );
    const host = atom && pidHost(atom.element);
    if (host) return host;
  }

  return null;
}

// An advisory change names a field, not a quote. The atom table already knows
// where every field is rendered, so the field itself is the position — this is
// the one rung that needs no text match and cannot be defeated by wording.
function normalizeFieldPath(value) {
  // Deal-terms paths are lease-relative (`freeRent`); a lease-var names itself
  // with the model root (`lease.freeRent`). Comparing them raw matches nothing.
  return String(value || "")
    .trim()
    .replace(/^lease\./, "");
}

function elementForFieldPath(index, fieldPath) {
  const path = normalizeFieldPath(fieldPath);
  if (!path) return null;

  const atom = (index.atomSpans || []).find(
    span => span.fieldPath && normalizeFieldPath(span.fieldPath) === path
  );
  return atom ? pidHost(atom.element) : null;
}

/**
 * A drafted clause is a block-level paragraph, and a block-level paragraph
 * inside a two-column definitions table is structurally wrong however well the
 * sentence reads. `locateInsertBlock` refuses a table anchor for exactly this
 * reason, and so does INSERT.
 *
 * The note ladder never had the rule because a note only had to be read, and
 * drafting through the ladder inherited the gap. In a live run it put riders
 * into Article 1's definitions table — beside LANDLORD, beside ADDRESS, beside
 * SHOPPING CENTER NAME — and welded "DEVELOPMENT" onto the front of the
 * heading next to one of them.
 *
 * A note is still placed there. Explaining something beside a table cell is
 * fine; adding a paragraph to one is not.
 */
function insideTable(element) {
  return Boolean(element && element.closest && element.closest("td, th"));
}

function lastBlock(root) {
  const blocks = root ? root.querySelectorAll("[pid]") : [];
  return blocks.length > 0 ? blocks[blocks.length - 1] : null;
}

export function resolveNoteAnchor(note, index, root) {
  const byAnchor = elementForQuote(index, note && note.anchor);
  if (byAnchor) return { element: byAnchor, rung: "anchor" };

  const byContext = elementForQuote(index, note && note.contextBefore);
  if (byContext) return { element: byContext, rung: "context" };

  const byField = elementForFieldPath(index, note && note.fieldPath);
  if (byField) return { element: byField, rung: "field" };

  const byAtom = elementForAtomReference(
    index,
    note && note.anchor,
    note && note.contextBefore
  );
  if (byAtom) return { element: byAtom, rung: "field" };

  const tail = lastBlock(root);
  return tail ? { element: tail, rung: "document-end" } : null;
}

/**
 * Turn drafter notes into the same `{ anchor, proposal }` shape
 * `integrateGeneratedInsertBlocks` already consumes, so a note reaches the
 * document through the identical builder, marker and annotation path as a
 * proposal. A note is a suggestion the drafter accepts or rejects; it is not a
 * separate rendering mode.
 */
export function buildNotePlacements(notes, index, { root }) {
  const placements = [];
  // Reported alongside the rungs so a run can be read as "how many of these
  // reached the drafter as language" rather than only "how many landed
  // somewhere". A note is the outcome we are trying to stop producing.
  let draftedCount = 0;
  const rungCounts = {
    anchor: 0,
    context: 0,
    field: 0,
    "document-end": 0,
    unplaced: 0
  };

  // The same sentence, twice, in the same paragraph.
  //
  // A lease writes a duration as "⟦f40:four⟧ (⟦f41:4⟧) months" — one value, two
  // atoms, because the word and the numeral are rendered separately. A model
  // reading that raises a conflict against each, and both carry the same drafted
  // sentence, so the drafter gets two identical riders in one place.
  //
  // Keyed on the anchor as well as the text, so this only ever collapses a
  // literal repeat. Two notes often share wording — every `not_editable` note
  // reads the same — and those are about different terms in different
  // paragraphs, which is information, not duplication.
  const placed = new Set();
  // Counts the rung only for a note that survives, so `rungCounts` keeps
  // summing to the number of placements, not the number considered.
  const isRepeat = (resolved, content) => {
    const key = `${resolved.element.getAttribute("pid") || ""} ${content}`;
    if (placed.has(key)) return true;
    placed.add(key);
    rungCounts[resolved.rung] += 1;
    return false;
  };

  (Array.isArray(notes) ? notes : []).forEach(note => {
    if (!note) return;

    const resolved = resolveNoteAnchor(note, index, root);
    if (!resolved) {
      rungCounts.unplaced += 1;
      return;
    }

    // A refusal we can draft through stops being a note here. The drafter gets
    // the term as a paragraph to accept, and the card carries the reason it
    // arrived as a paragraph rather than as an edit in place.
    const placeable = !insideTable(resolved.element);
    const draftedBecause = DRAFTABLE_REFUSALS[note.reason];
    const clause = collapseWhitespace(note.fallbackContent);
    if (draftedBecause && clause && placeable) {
      if (isRepeat(resolved, clause)) return;
      draftedCount += 1;
      placements.push({
        anchor: resolved.element,
        rung: resolved.rung,
        note,
        insertableContent: clause,
        proposal: {
          kind: "insert_block",
          content: clause,
          sourceQuote: collapseWhitespace(note.sourceQuote) || clause,
          rationale: note.rationale,
          draftedBecause,
        },
      });
      return;
    }

    // A clause that was already drafted and only lost its way there.
    const orphaned = collapseWhitespace(note.content);
    if (
      ANCHOR_FAILURE_REASONS.includes(note.reason) &&
      orphaned &&
      placeable &&
      resolved.rung !== "document-end"
    ) {
      if (isRepeat(resolved, orphaned)) return;
      draftedCount += 1;
      placements.push({
        anchor: resolved.element,
        rung: resolved.rung,
        note,
        insertableContent: orphaned,
        proposal: {
          kind: "insert_block",
          content: orphaned,
          sourceQuote: collapseWhitespace(note.sourceQuote) || orphaned,
          rationale: note.rationale,
          draftedBecause: ANCHOR_FAILURE_NOTICE
        }
      });
      return;
    }

    const content = noteBodyText(note);
    if (isRepeat(resolved, content)) return;
    placements.push({
      anchor: resolved.element,
      rung: resolved.rung,
      note,
      // What INSERT should place if the drafter re-anchors this note by hand:
      // the clause the refused proposal wanted, not the bracketed note about
      // it. Committing "[DRAFTING NOTE — …]" into a lease as permanent prose is
      // the opposite of the intent (lp-006 §6a). Falls back to the note text
      // only when the proposal carried no language at all.
      insertableContent: collapseWhitespace(note.content) || content,
      proposal: {
        kind: "insert_block",
        content,
        // createGeneratedAnnotations skips any item with an empty source quote,
        // and an inserted marker with no annotation card cannot be accepted or
        // rejected at all — `_accept` needs the card to resolve against. A note
        // whose proposal never cited the source still has to be reviewable, so
        // it cites itself rather than becoming an orphan in the document.
        sourceQuote: collapseWhitespace(note.sourceQuote) || content,
        rationale: note.rationale
      }
    });
  });

  return { placements, rungCounts, draftedCount };
}

/**
 * Advisory deal-term changes carry a field path rather than a lease quote:
 * phase one already decided the value has nowhere to go. They still describe a
 * term the source document agreed to, so they travel the same route as every
 * other note rather than staying behind in the panel.
 */
export function advisoryChangesAsNotes(changes) {
  return (Array.isArray(changes) ? changes : [])
    .filter(change => change && change.path)
    .map(change => ({
      kind: "drafter_note",
      reason: change.advisoryReason || "no_control",
      message: change.message,
      sourceQuote: change.sourceQuote || change.summary || String(change.after),
      rationale: change.rationale || "",
      // Carried so a value with nowhere to go in the form still reaches the
      // lease as language. Without it the drafter is told a term applies, told
      // the form cannot hold it, and left to write it themselves.
      fallbackContent: change.fallbackContent || "",
      fieldPath: change.path
    }));
}
